import { useState, useEffect, useRef } from 'preact/hooks'
import { Search, Loader2, X } from 'lucide-preact'
import { BRAND_CLASSES } from '@/constants/theme'

export interface TypeAheadOption<T = unknown> {
  /** Unique key for the option */
  key: string
  /** Display label */
  label: string
  /** Original data object */
  data: T
  /** Optional custom renderer for the option */
  render?: () => preact.ComponentChildren
}

export interface TypeAheadDropdownProps<T = unknown> {
  /** Field label */
  label?: string
  /** Whether field is required */
  required?: boolean
  /** Field name */
  name?: string
  /** Placeholder text for the input */
  placeholder?: string
  /** Error message to display */
  error?: string
  /** Marks the input invalid with the error border, for a form that says why in a line of its own */
  invalid?: boolean
  /** Id of the element that describes the input, such as that line */
  describedBy?: string
  /** Helper text to display below input */
  helperText?: string
  /** Minimum characters before triggering search (default: 3) */
  minSearchLength?: number
  /** Whether the dropdown is disabled */
  disabled?: boolean
  /** Custom class for container */
  containerClassName?: string
  /** Custom class for input */
  inputClassName?: string
  /** Current search value (controlled) */
  searchValue: string
  /** Search value change handler */
  onSearchChange: (value: string) => void
  /** Whether search is in progress */
  loading?: boolean
  /** Search results to display */
  options: TypeAheadOption<T>[]
  /** Handler when an option is selected */
  onSelect: (option: TypeAheadOption<T>) => void
  /** Handler to clear the search */
  onClear?: () => void
  /** Whether to show clear button */
  showClearButton?: boolean
  /** Optional message when no results found */
  noResultsMessage?: string
  /** Optional message before minimum search length is reached */
  minLengthMessage?: string
}

/**
 * TypeAheadDropdown - A searchable dropdown component
 *
 * Features:
 * - Controlled search input (caller is responsible for debouncing via useDebouncedValue)
 * - Minimum character requirement before search
 * - Loading state during search
 * - Custom option rendering
 * - Click outside to close
 * - Keyboard navigation (arrow keys, enter, escape)
 *
 * @example
 * ```tsx
 * const [searchValue, setSearchValue] = useState('')
 * const [loading, setLoading] = useState(false)
 * const [results, setResults] = useState<User[]>([])
 *
 * // Fetch results when search value changes (after debounce)
 * useEffect(() => {
 *   if (searchValue.length >= 3) {
 *     setLoading(true)
 *     api.searchUsers(searchValue).then(data => {
 *       setResults(data)
 *       setLoading(false)
 *     })
 *   }
 * }, [searchValue])
 *
 * <TypeAheadDropdown
 *   label="Search Users"
 *   placeholder="Type to search..."
 *   searchValue={searchValue}
 *   onSearchChange={setSearchValue}
 *   loading={loading}
 *   options={results.map(user => ({
 *     key: user.id,
 *     label: `${user.firstName} ${user.lastName}`,
 *     data: user,
 *     render: () => (
 *       <div className="flex items-center gap-2">
 *         <img src={user.avatar} className="w-6 h-6 rounded-full" />
 *         <span>{user.firstName} {user.lastName}</span>
 *       </div>
 *     )
 *   }))}
 *   onSelect={(option) => console.log('Selected:', option.data)}
 *   onClear={() => setSearchValue('')}
 * />
 * ```
 */
export function TypeAheadDropdown<T = unknown>({
  label,
  required = false,
  name,
  placeholder = 'Type to search...',
  error,
  invalid = false,
  describedBy,
  helperText,
  minSearchLength = 3,
  disabled = false,
  containerClassName = '',
  inputClassName = '',
  searchValue,
  onSearchChange,
  loading = false,
  options,
  onSelect,
  onClear,
  showClearButton = true,
  noResultsMessage = 'No results found',
  minLengthMessage
}: TypeAheadDropdownProps<T>): preact.VNode {
  const [isOpen, setIsOpen] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const hasError = !!error || invalid
  const rightPadding = showClearButton && searchValue ? 'pr-10' : 'pr-3'
  const inputClasses = `w-full pl-10 ${rightPadding} py-2 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${
    hasError
      ? `${BRAND_CLASSES.INPUT.ERROR}`
      : `${BRAND_CLASSES.INPUT.NORMAL} ${BRAND_CLASSES.FOCUS.PRIMARY}`
  } ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${inputClassName}`

  const showMinLengthMessage = searchValue.length > 0 && searchValue.length < minSearchLength
  const showDropdown = isOpen && (loading || options.length > 0 || showMinLengthMessage || (searchValue.length >= minSearchLength && options.length === 0))

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
        setFocusedIndex(-1)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Open the dropdown as the user types past the minimum length. An empty field
  // must NOT auto-open: for browse-style pickers (minSearchLength=0) that would
  // pop the list open on mount and keep it open. When empty, leave the open state
  // to focus / click-outside instead of forcing it here.
  useEffect(() => {
    if (searchValue.length === 0) return
    if (searchValue.length >= minSearchLength || showMinLengthMessage) {
      setIsOpen(true)
    } else {
      setIsOpen(false)
    }
  }, [searchValue, minSearchLength, showMinLengthMessage])

  // Reset focused index when options change
  useEffect(() => {
    setFocusedIndex(-1)
  }, [options])

  // Keyboard navigation
  const handleKeyDown = (e: KeyboardEvent) => {
    if (!isOpen) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setFocusedIndex(prev => (prev < options.length - 1 ? prev + 1 : prev))
        break
      case 'ArrowUp':
        e.preventDefault()
        setFocusedIndex(prev => (prev > 0 ? prev - 1 : -1))
        break
      case 'Enter':
        e.preventDefault()
        if (focusedIndex >= 0 && focusedIndex < options.length) {
          handleSelect(options[focusedIndex])
        }
        break
      case 'Escape':
        e.preventDefault()
        setIsOpen(false)
        setFocusedIndex(-1)
        inputRef.current?.blur()
        break
    }
  }

  const handleSelect = (option: TypeAheadOption<T>) => {
    onSelect(option)
    setIsOpen(false)
    setFocusedIndex(-1)
    // Don't clear search value - let parent component decide
  }

  const handleClearClick = () => {
    onSearchChange('')
    if (onClear) onClear()
    setIsOpen(false)
    setFocusedIndex(-1)
    inputRef.current?.focus()
  }

  return (
    <div className={containerClassName} ref={containerRef}>
      {label && (
        <label
          htmlFor={name}
          className={`block text-sm font-medium ${BRAND_CLASSES.INPUT.LABEL} mb-2`}
        >
          {label}
          {required && <span className={`${BRAND_CLASSES.INPUT.REQUIRED} ml-1`}>*</span>}
        </label>
      )}

      <div className="relative">
        {/* Search Icon */}
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-4 w-4 text-slate-400 dark:text-slate-500" />
        </div>

        {/* Input */}
        <input
          ref={inputRef}
          type="text"
          id={name}
          name={name}
          aria-invalid={hasError || undefined}
          aria-describedby={describedBy}
          className={inputClasses}
          placeholder={placeholder}
          value={searchValue}
          onChange={(e) => onSearchChange((e.target as HTMLInputElement).value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (searchValue.length >= minSearchLength || showMinLengthMessage) {
              setIsOpen(true)
            }
          }}
          disabled={disabled}
          autoComplete="off"
        />

        {/* Clear Button */}
        {showClearButton && searchValue && !disabled && (
          <button
            type="button"
            onClick={handleClearClick}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        )}

        {/* Dropdown */}
        {showDropdown && (
          <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-60 overflow-auto">
            {loading ? (
              <div className="flex items-center justify-center gap-2 p-4 text-sm text-slate-600 dark:text-slate-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Searching...</span>
              </div>
            ) : showMinLengthMessage ? (
              <div className="p-4 text-sm text-slate-500 dark:text-slate-400">
                {minLengthMessage || `Type at least ${minSearchLength} characters to search`}
              </div>
            ) : options.length === 0 ? (
              <div className="p-4 text-sm text-slate-500 dark:text-slate-400">
                {noResultsMessage}
              </div>
            ) : (
              <ul className="py-1">
                {options.map((option, index) => (
                  <li key={option.key}>
                    <button
                      type="button"
                      onClick={() => handleSelect(option)}
                      className={`w-full text-left px-4 py-2.5 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors ${
                        index === focusedIndex ? 'bg-slate-100 dark:bg-slate-700' : ''
                      }`}
                    >
                      {option.render ? option.render() : (
                        <span className="text-sm text-slate-900 dark:text-white">
                          {option.label}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {error && (
        <p className={`mt-1 text-sm ${BRAND_CLASSES.INPUT.ERROR_TEXT}`}>{error}</p>
      )}

      {helperText && !error && (
        <p className={`mt-1 text-xs ${BRAND_CLASSES.INPUT.HELPER}`}>{helperText}</p>
      )}
    </div>
  )
}
